import React from "react";
import { Skeleton } from "app/components/Skeleton";
import Progress from "app/components/Progress";
import LetterCard from "app/components/LetterCard";
import LazyImage from "app/components/LazyImage";
import ReactIcon from "app/components/ReactIcon";
import { getInitials } from "utils/helpers";
import { IAvatar } from "app/containers/Features/containers/TeamSettings/container/MySetting/types";

interface ProjectItemProps {
  item: any;
  index: number;
}

const renderLetter = (avatar: IAvatar | undefined, name: string) => {
  if (avatar?.file) {
    return (
      <LazyImage
        src={avatar.file}
        alt={name}
        className="size-[12px] object-cover"
      />
    );
  }
  if (avatar?.icon) {
    return <ReactIcon icon={avatar.icon} />;
  }
  return getInitials(name);
};

export const ProjectItem: React.FC<ProjectItemProps> = ({ item, index }) => {
  const isLoadingDetails = item.name === "Unknown Project";

  return (
    <div
      key={index}
      className="flex h-auto shrink-0 items-center justify-between gap-2.5 rounded border border-gray-100 px-4 py-3"
    >
      {/* Left section */}
      <div className="flex items-center gap-2.5">
        {isLoadingDetails ? (
          <Skeleton className="size-[25px] rounded-[2.08px]" />
        ) : (
          <LetterCard
            className="!size-[25px] !min-w-[25px] !min-h-[25px] rounded-[2.08px]"
            backgroundColor={item.iconColor}
            letterClassName="text-secondary text-[16px] font-bold"
            letter={renderLetter(item.avatar, item.name)}
          />
        )}

        <div className="flex flex-col gap-1">
          {isLoadingDetails ? (
            <Skeleton className="h-[20px] w-[100px] rounded" />
          ) : (
            <span className="text-sm font-medium text-black">
              {item.name}
            </span>
          )}

          {isLoadingDetails ? (
            <Skeleton className="h-[14px] w-[80px] rounded" />
          ) : (
            <span className="text-xs font-normal text-secondary">
              {item.description}
            </span>
          )}
        </div>
      </div>

      {/* Right section */}
      <div className="flex h-[58px] w-[215px] flex-col gap-2">
        {/* Developers Progress */}
        <div className="flex h-[25px] w-full flex-col justify-center">
          <span className="text-[10px] text-secondary">
            Developers progress:
          </span>

          <div className="relative w-full">
            <Progress
              progress={item.developerProgress}
              borderRadius={2}
              containerClassName="h-[14px] bg-[#F3F4F6]"
              progressClassName="bg-primary"
            />
            <span
              className="absolute left-0 top-0 flex h-full items-center justify-center text-[9px] font-medium text-white"
              style={{
                width: `${item.developerProgress}%`,
              }}
            >
              {item.developerProgress}%
            </span>
          </div>
        </div>

        {/* QA Progress */}
        <div className="flex h-[25px] w-full flex-col justify-center">
          <span className="text-[10px] text-secondary">
            QAs progress:
          </span>

          <div className="relative w-full">
            <Progress
              progress={item.qaProgress}
              borderRadius={2}
              containerClassName="h-[14px] bg-[#F3F4F6]"
              progressClassName="bg-[#F472B6]"
            />
            <span
              className="absolute left-0 top-0 flex h-full items-center justify-center text-[9px] font-medium text-white"
              style={{
                width: `${item.qaProgress}%`,
              }}
            >
              {item.qaProgress}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProjectItem);
